<template>
  <panel
    class="invoice"
    :show-footer="!isGift"
    @close="closePanel"
    v-if="detail && subi18n"
  >
    <template slot="header">
      <div class="">
        <div class="text-sm">
          {{ detail.product }} {{ detail.number ? "#" + detail.number : "" }}
        </div>
        <div
          v-html="
            isGift ? detail.credits : detail.credits + ' / ' + detail.amount
          "
        ></div>
      </div>

      <div class="" v-if="isGift">
        <font-awesome-icon
          :icon="['fas', 'gift']"
          class="faa-pulse animated"
          fixed-width
        ></font-awesome-icon>
      </div>
      <div class="" v-if="isTransfer">
        <font-awesome-icon
          icon="exchange-alt"
          class="faa-pulse animated"
          fixed-width
        ></font-awesome-icon>
      </div>
      <div class="flex items-end justify-end" v-else>
        <zoom-center-transition group class="flex">
          <ranch-button
            :label="subi18n.actions.pay"
            :icon="['fas', 'credit-card']"
            @click="pay"
            v-if="!isPaid && detail.status.id !== 'proof_sent'"
            key="invoice_pay"
          ></ranch-button>

          <ranch-button
            :label="subi18n.actions.download"
            icon="file-pdf"
            text-size="text-xs"
            v-if="hasInvoice"
            key="invoice_download"
            :endpoint="downloadURL"
          ></ranch-button>

          <ranch-button
            :label="subi18n.actions.delete"
            icon="trash-alt"
            action="warning"
            @click="deleteRefill"
            v-if="!isPaid && !hasInvoice && detail.status.id !== 'proof_sent'"
            key="invoice_delete"
          ></ranch-button>
        </zoom-center-transition>
      </div>
    </template>
    <template slot="main">
      <fade-transition group>
        <list-item
          :label="
            detail.amounts.credits.label +
            ' ' +
            subi18n.amounts.rendering_credits
          "
          v-if="!isGift"
          key="invoice_credits"
        >
          <span v-html="detail.amounts.credits.data"></span>
        </list-item>

        <list-item
          v-if="detail.promotion"
          :label="detail.promotion"
          class="bg-promo-bg text-promo-fg rounded-t -mb-1"
          key="invoice_promotion"
        ></list-item>
        <list-item
          v-if="detail.promotion"
          :label="freeCreditsLabel"
          class="bg-promo-bg text-promo-fg rounded-b"
          key="invoice_free_credits"
        ></list-item>
        <list-item
          v-else-if="showFreeCreditsLine"
          :label="freeCreditsLabel"
          key="invoice_free_credits"
        >
          <span v-html="detail.amounts.free_credits.data"></span>
        </list-item>
        <list-item
          :label="
            subi18n.amounts.vat + '&nbsp;(' + detail.amounts.vat.label + ')'
          "
          v-if="!isGift"
          key="invoice_vat"
        >
          <span v-html="detail.amounts.vat.data"></span>
        </list-item>
        <list-item
          :label="'<b>' + subi18n.amounts.total + '</b>'"
          key="invoice_total"
        >
          <span v-html="detail.amounts.total.data" class="font-bold"></span>
        </list-item>
        <list-item
          :label="subi18n.amounts.payment_date"
          v-if="detail.isPaid && !isGift"
          key="invoice_date"
        >
          <span v-html="detail.amounts.paid.data"></span>
        </list-item>
      </fade-transition>
    </template>
    <template slot="footer">
      <div class="flex justify-end pr-1">
        <fade-transition>
          <ranch-button
            :label="subi18n.actions.support"
            icon="envelope-open-text"
            action="action-secondary shadow-md"
            :route="supportLocation"
          ></ranch-button>
        </fade-transition>
      </div>
    </template>
  </panel>
</template>

<script>
import axios from "axios";
import { handleAxiosError } from "@/boot/functions";

import ListItem from "../Design/ListItem";
import Panel from "../Design/Panel";
import NewRefillModalContent from "./NewRefill";
import NewRefillModalConfig from "../Modals/configs/newrefill";

export default {
  props: ["i18n", "id"],
  components: {
    ListItem,
    Panel,
  },
  methods: {
    closePanel() {
      this.$router.push({
        name: "invoice.welcome",
        params: { locale: this.locale },
      });
    },

    deleteRefill() {
      if (confirm(this.subi18n.actions.delete_confirm)) {
        axios
          .delete(this.detail.routes.delete)
          .then(() => {
            this.$store.dispatch("safe/getPaginator");
            this.$router.push({
              name: "invoice.welcome",
              params: { locale: this.locale },
            });
          })
          .catch(handleAxiosError);
      }
    },

    pay() {
      this.$modal.show(
        NewRefillModalContent,
        {
          step: 3,
          existingRefill: this.detail,
        },
        NewRefillModalConfig
      );
    },
    loadDetails() {
      if (!this.detail || this.detail.id != this.id) {
        this.$store.dispatch("safe/getDetail", this.id);
      }
    },
  },
  computed: {
    subi18n() {
      return this.i18n.invoice;
    },
    detail() {
      return this.$store.getters["safe/getSelectedRefill"];
    },

    isGift() {
      if (this.detail !== undefined) {
        return this.detail.isGift;
      }

      return false;
    },

    isTransfer() {
      if (this.detail !== undefined) {
        return this.detail.isTransfer;
      }

      return false;
    },

    isPaid() {
      if (this.detail !== undefined) {
        return this.detail.isPaid;
      }

      return false;
    },

    showFreeCreditsLine() {
      if (this.isGift) {
        return true;
      }

      return this.detail.amounts.bonusRate > 0;
    },

    freeCreditsLabel() {
      if (this.detail !== undefined) {
        let label =
          this.detail.amounts.free_credits.label +
          " " +
          this.subi18n.amounts.free_rendering_credits;
        if (this.detail.amounts.bonusRate > 0) {
          label += " (" + this.detail.amounts.bonusRate + "%)";
        }

        return label;
      }

      return "";
    },

    hasInvoice() {
      return this.detail.number > 0;
    },

    locale() {
      return this.$route.params.locale;
    },

    supportLocation() {
      return {
        query: Object.assign({}, this.$route.query, {
          supportModal: "credits",
          invoice_ids: "ID: " + this.detail.id,
        }),
      };
    },

    downloadURL() {
      return this.$store.state.safe.routes.invoices.download.replace(
        ":id",
        this.detail.id
      );
    },
  },
  watch: {
    id() {
      this.loadDetails();
    },
    detail() {
      this.$nextTick(() => {
        this.$el.scrollIntoView();
      });
    },
  },
  mounted() {
    this.loadDetails();
  },
};
</script>
